/**
 * Tâche 1252 (Eric 2026-06-28) — « Écarts de transcription ».
 *
 * La bannière annonçait « couverture 78 % » sans jamais dire QUELS passages
 * manquent : impossible de corriger à la main ce qu'on ne peut pas lire. Cette
 * fenêtre liste les trois sortes d'écarts avec leur provenance, et permet de
 * tout copier ou de télécharger un fichier.
 */
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { ArrowsDownUp, Copy, DownloadSimple, FloppyDisk, PlayCircle, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { Button } from "../ui/button";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Tâche 1375 — Cette table vit HORS du composant : elle ne peut donc pas
// appeler le traducteur (un appel hors du hook rend l'écran blanc —
// leçon 1373). Les deux langues vivent sur la même entrée, motif 1324b : deux
// tables jumelles divergeraient dès le premier ajout de sorte d'écart.
const KIND_STYLE = {
  fireflies_absent_whisper: {
    label: "Whisper a manqué ce passage",
    label_en: "Whisper missed this passage",
    cls: "bg-amber-100 text-amber-900",
  },
  perdu_a_la_fusion: {
    label: "Perdu à la fusion",
    label_en: "Lost in the merge",
    cls: "bg-red-100 text-red-900",
  },
  trou_sans_texte: {
    label: "Aucun texte sur la plage",
    label_en: "No text over this range",
    cls: "bg-zinc-100 text-zinc-700",
  },
};

const kindLabel = (style, lang) => (lang === "en" ? style.label_en : style.label) || style.label;

const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.round(s % 60)).padStart(2, "0")}`;

// Tâche 1255 — Un écart de 2 minutes coûte plus cher qu'un de 6 secondes :
// le badge de durée change de ton pour que le plus lourd saute aux yeux.
const severityCls = (sec) => {
  if (sec >= 30) return "bg-red-100 text-red-900";
  if (sec >= 10) return "bg-amber-100 text-amber-900";
  return "bg-zinc-100 text-zinc-700";
};

// Priorité : ce qui reste à faire d'abord, du plus long au plus court.
// Les écarts déjà comblés à la main descendent en bas (choix d'Eric).
const byPriority = (a, b) => {
  if (!!a.filled !== !!b.filled) return a.filled ? 1 : -1;
  return (b.missing_sec || 0) - (a.missing_sec || 0) || a.start - b.start;
};

export const CoverageGapsModal = ({ videoId, onClose, seekTo, onFilled }) => {
  const { tr, lang } = useLang();
  // Tâche 1253 — Lister les écarts ne suffit pas : il faut pouvoir les
  // COMBLER à l'endroit exact. Un clic replace la vidéo au début du passage
  // et met en pause : on réécoute, on écrit, on enregistre.
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [sortMode, setSortMode] = useState("priorite");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/videos/${videoId}/coverage-gaps`, {
        withCredentials: true,
        timeout: 60000,
      });
      setReport(data);
    } catch (e) {
      setError(e.response?.data?.detail || tr("Lecture des écarts impossible", "Could not read the gaps"));
    }
  }, [videoId]);

  useEffect(() => {
    load();
  }, [load]);

  const goToGap = (gap) => {
    if (seekTo) seekTo(gap.start);
    // `seekTo` relance la lecture : on met en pause pour laisser écrire.
    setTimeout(() => {
      const el = document.querySelector("video");
      if (el) el.pause();
    }, 150);
    setEditing(`${gap.kind}-${gap.start}`);
    setDraft(gap.filled_text || gap.text || "");
  };

  const saveFill = async (gap) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/coverage-gaps/fill`,
        { start: gap.start, end: gap.end, text: draft, kind: gap.kind },
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(tr("Passage ajouté à la transcription", "Passage added to the transcript"));
      setEditing(null);
      await load();
      if (onFilled) onFilled();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Enregistrement impossible", "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const injectableCount = (report?.gaps || []).filter(
    (g) => g.kind === "fireflies_absent_whisper" && !g.filled && (g.text || "").trim(),
  ).length;

  /** Tâche 1279 — Passe linguistique sur les SEULS passages injectés. */
  const cleanInjectedText = async () => {
    try {
      const { data: proposed } = await axios.post(
        `${API}/api/videos/${videoId}/propose-linguistic-corrections?scope=injected`,
        {},
        { withCredentials: true, timeout: 180000 },
      );
      const corrections = proposed.corrections || [];
      if (corrections.length === 0) {
        toast.success(tr("Le texte injecté n'avait rien à corriger.", "The injected text had nothing to fix."));
        return;
      }
      await axios.post(
        `${API}/api/videos/${videoId}/apply-linguistic-corrections`,
        { corrections },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `${corrections.length} correction(s) appliquée(s) sur le texte injecté`
        : `${corrections.length} correction(s) applied to the injected text`);
      if (onFilled) onFilled();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Nettoyage impossible", "Could not clean up"));
    }
  };

  const injectAllFireflies = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? `Injecter ${injectableCount} passage(s) entendu(s) par Fireflies dans la transcription fusionnée ? Chaque ajout reste retirable un par un.`
        : `Inject ${injectableCount} passage(s) heard by Fireflies into the merged transcript? Each addition can still be removed one by one.`,
      { tone: "default", confirmLabel: tr("Injecter", "Inject") },
    );
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/coverage-gaps/fill-all-fireflies`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      toast.success(lang === "fr"
        ? `${data.injected} passage(s) injecté(s) dans la fusion`
        : `${data.injected} passage(s) injected into the merge`);
      await load();
      if (onFilled) onFilled();
      // Tâche 1279 (Eric 2026-06-29) — Le texte importé arrive sans
      // ponctuation soignée : c'est LE moment de le nettoyer, et seulement
      // lui. On annonce l'appel IA avant de le déclencher.
      if (data.injected > 0) {
        const clean = await confirmDialog(
          lang === "fr"
            ? `Nettoyer maintenant les ${data.injected} passage(s) ajouté(s) (orthographe, accents, ponctuation) ? 1 appel IA facturé, uniquement sur le texte injecté.`
            : `Clean up the ${data.injected} added passage(s) now (spelling, accents, punctuation)? 1 billed AI call, on the injected text only.`,
          { tone: "warning", confirmLabel: tr("Nettoyer", "Clean up") },
        );
        if (clean) await cleanInjectedText();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Injection en lot impossible", "Could not inject the batch"));
    } finally {
      setSaving(false);
    }
  };

  const injectFirefliesText = async (gap) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/coverage-gaps/fill`,
        { start: gap.start, end: gap.end, text: gap.text,
          speaker: gap.speaker || "", kind: gap.kind },
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(tr("Texte Fireflies injecté dans la fusion", "Fireflies text injected into the merge"));
      await load();
      if (onFilled) onFilled();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Injection impossible", "Could not inject"));
    } finally {
      setSaving(false);
    }
  };

  const removeFill = async (gap) => {    setSaving(true);
    try {
      await axios.delete(
        `${API}/api/videos/${videoId}/coverage-gaps/fill?start=${gap.start}`,
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(tr("Ajout manuel retiré", "Manual addition removed"));
      await load();
      if (onFilled) onFilled();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Retrait impossible", "Could not remove"));
    } finally {
      setSaving(false);
    }
  };

  const copyAll = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/videos/${videoId}/coverage-gaps?format=text`,
        { withCredentials: true, responseType: "text" },
      );
      await navigator.clipboard.writeText(data);
      toast.success(tr("Écarts copiés — prêts à coller", "Gaps copied — ready to paste"));
    } catch {
      toast.error(tr("Copie impossible", "Could not copy"));
    }
  };

  const downloadCsv = () => {
    window.open(`${API}/api/videos/${videoId}/coverage-gaps?format=csv`, "_blank");
  };

  return createPortal(
    <div className="fixed inset-0 z-[300] bg-black/70 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="coverage-gaps-modal"
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-zinc-900">{tr("Écarts de transcription", "Transcript gaps")}</h3>
            <p className="text-xs text-zinc-600 mt-1" data-testid="coverage-gaps-summary">
              {report
                ? (lang === "fr"
                    ? `${report.total} écart(s) · ${report.total_missing_sec} s au total · Whisper a manqué ${report.by_kind?.fireflies_absent_whisper || 0}, fusion a perdu ${report.by_kind?.perdu_a_la_fusion || 0}, ${report.by_kind?.trou_sans_texte || 0} plage(s) sans texte`
                    : `${report.total} gap(s) · ${report.total_missing_sec} s in total · Whisper missed ${report.by_kind?.fireflies_absent_whisper || 0}, the merge lost ${report.by_kind?.perdu_a_la_fusion || 0}, ${report.by_kind?.trou_sans_texte || 0} range(s) without text`)
                : error || tr("Analyse…", "Analysing…")}
            </p>
            {/* Bogue 1271 (Eric 2026-06-29) — On annonçait des passages
                « manqués » que l'autre source avait bel et bien. Chaque écart
                est maintenant confirmé par le texte ; on affiche combien ont
                été écartés pour que le filet soit vérifiable. */}
            {report?.verified_dropped > 0 && (
              <p className="text-[11px] text-emerald-700 mt-0.5" data-testid="coverage-gaps-verified">
                {lang === "fr"
                  ? `${report.verified_dropped} écart(s) écarté(s) après vérification : le texte existe bien dans l'autre source.`
                  : `${report.verified_dropped} gap(s) dropped after checking: the text does exist in the other source.`}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="coverage-gaps-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 flex items-center gap-2 border-b border-zinc-100">
          <Button variant="outline" size="sm" onClick={copyAll} data-testid="coverage-gaps-copy-btn">
            <Copy size={15} className="mr-2" /> {tr("Copier la liste", "Copy the list")}
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="coverage-gaps-csv-btn">
            <DownloadSimple size={15} className="mr-2" /> {tr("Fichier .csv", ".csv file")}
          </Button>
          {/* Tâche 1277 (Eric 2026-06-29) — Un clic par passage (1276) ne
              tient pas sur une vidéo qui en compte cent. Le serveur recalcule
              les écarts et injecte tout ce que Fireflies a entendu. */}
          {injectableCount > 0 && (
            <Button
              size="sm"
              onClick={injectAllFireflies}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="coverage-gaps-inject-all-btn"
              title={tr("Injecter d'un coup tous les passages entendus par Fireflies et manqués par Whisper", "Inject in one go every passage heard by Fireflies and missed by Whisper")}
            >
              <DownloadSimple size={15} className="mr-2" />
              {tr("Injecter tout Fireflies", "Inject all Fireflies")} ({injectableCount})
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-zinc-500 hidden sm:inline">{tr("Affichage :", "View:")}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortMode(sortMode === "priorite" ? "chrono" : "priorite")}
              data-testid="coverage-gaps-sort-toggle"
            >
              <ArrowsDownUp size={15} className="mr-2" />
              {sortMode === "priorite"
                ? tr("Les plus longs d'abord", "Longest first")
                : tr("Ordre chronologique", "Chronological order")}
            </Button>
          </div>
        </div>

        {sortMode === "priorite" && (
          <p className="px-5 pb-2 text-[11px] text-zinc-500" data-testid="coverage-gaps-sort-hint">
            {tr(
              "Classés par durée manquante : corrigez le #1 en premier. Les fichiers exportés restent en ordre chronologique.",
              "Sorted by missing duration: fix #1 first. Exported files stay in chronological order.",
            )}
          </p>
        )}

        <div className="overflow-y-auto px-5 py-3 space-y-3" data-testid="coverage-gaps-list">
          {report?.gaps?.length === 0 && (
            <p className="text-sm text-teal-700">
              {lang === "fr"
                ? `Aucun écart au-delà des seuils (${report.thresholds?.gap_min_seconds} s pour un passage, ${report.thresholds?.silence_min_seconds} s pour une plage sans texte).`
                : `No gap beyond the thresholds (${report.thresholds?.gap_min_seconds} s for a passage, ${report.thresholds?.silence_min_seconds} s for a range without text).`}
            </p>
          )}
          {(sortMode === "priorite"
            ? [...(report?.gaps || [])].sort(byPriority)
            : [...(report?.gaps || [])].sort((a, b) => a.start - b.start)
          ).map((g, i) => {
            const style = KIND_STYLE[g.kind] || KIND_STYLE.trou_sans_texte;
            return (
              <div key={`${g.kind}-${g.start}-${i}`} className="border border-zinc-200 rounded-lg p-3">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  {sortMode === "priorite" && !g.filled && (
                    <span
                      className="font-mono font-bold text-zinc-400"
                      data-testid={`gap-rank-${i}`}
                    >
                      #{i + 1}
                    </span>
                  )}
                  <span className="font-mono font-semibold text-zinc-900">
                    {mmss(g.start)} → {mmss(g.end)}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${style.cls}`}>
                    {kindLabel(style, lang)}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full font-semibold ${severityCls(g.missing_sec)}`}
                    data-testid={`gap-missing-${i}`}
                  >
                    {g.missing_sec} {tr("s manquantes", "s missing")}
                  </span>
                  <span className="text-zinc-500">{g.source}</span>
                </div>
                <p className="text-sm text-zinc-800 mt-1.5 whitespace-pre-wrap">
                  {g.text || tr("(aucun texte sur cette plage)", "(no text over this range)")}
                </p>

                {g.filled && editing !== `${g.kind}-${g.start}` && (
                  <div
                    className="mt-2 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-2"
                    data-testid={`gap-filled-${i}`}
                  >
                    <p className="text-sm text-teal-900 whitespace-pre-wrap">{g.filled_text}</p>
                    <p className="text-[11px] text-teal-700 mt-1">
                      {lang === "fr"
                        ? `Ajout manuel de ${g.filled_by} le ${(g.filled_at || "").slice(0, 16).replace("T", " ")}`
                        : `Added by hand by ${g.filled_by} on ${(g.filled_at || "").slice(0, 16).replace("T", " ")}`}
                    </p>
                  </div>
                )}

                {editing === `${g.kind}-${g.start}` ? (
                  <div className="mt-2">
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={3}
                      placeholder={tr("Écrivez ce qui a été dit pendant cette plage…", "Write what was said during this range…")}
                      className="w-full text-sm border border-zinc-300 rounded-md px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
                      data-testid={`gap-fill-input-${i}`}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={() => saveFill(g)}
                        disabled={saving || !draft.trim()}
                        data-testid={`gap-fill-save-${i}`}
                      >
                        <FloppyDisk size={15} className="mr-2" /> {tr("Enregistrer", "Save")}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(null)}
                              data-testid={`gap-fill-cancel-${i}`}>
                        {tr("Annuler", "Cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToGap(g)}
                      data-testid={`gap-goto-${i}`}
                    >
                      <PlayCircle size={15} className="mr-2" />
                      {g.filled
                        ? tr("Corriger le texte", "Fix the text")
                        : tr("Écouter et écrire", "Listen and write")}
                    </Button>
                    {/* Tâche 1276 (Eric 2026-06-29) — Quand Whisper a manqué
                        un passage, Fireflies l'a déjà écrit : le recopier à la
                        main était du travail pour rien. Un clic l'injecte dans
                        la fusion, marqué comme ajout (on peut le retirer). */}
                    {!g.filled && g.kind === "fireflies_absent_whisper" && (g.text || "").trim() && (
                      <Button
                        size="sm"
                        onClick={() => injectFirefliesText(g)}
                        disabled={saving}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid={`gap-inject-fireflies-${i}`}
                        title={tr("Injecter le texte entendu par Fireflies dans la transcription fusionnée", "Inject the text heard by Fireflies into the merged transcript")}
                      >
                        <DownloadSimple size={15} className="mr-2" /> {tr("Injecter le texte Fireflies", "Inject the Fireflies text")}
                      </Button>
                    )}
                    {g.filled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFill(g)}
                        disabled={saving}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`gap-fill-remove-${i}`}
                      >
                        <Trash size={15} className="mr-2" /> {tr("Retirer l'ajout", "Remove the addition")}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CoverageGapsModal;
